import PillLoadingBox from 'LuxKit/PillLoadingBox'
import ModalContext from 'contexts/ModalContext'
import React, { MouseEventHandler, useCallback, useContext } from 'react'
import BusinessTravellerHotelCreditsOverBudgetInfoModal from './BusinessTravellerOfferHotelCreditsOverBudgetInfoModal'
import useBusinessTravellerItemPolicyOfferCreditFetcher from 'businessTraveller/hooks/useBusinessTravellerItemPolicyOfferCreditFetcher'
import PriceRowBusinessTravellerCredit from 'LuxKit/PricePoints/BusinessTraveller/PriceRowBusinessTravellerCredit'
import PriceRowBusinessTravellerExceededBudget from 'LuxKit/PricePoints/BusinessTraveller/PriceRowBusinessTravellerExceededBudget'
import BusinessTravellerOfferCreditsInfoModal from './BusinessTravellerOfferCreditsInfoModal'
import PriceRowBusinessTravellerUnderBudget from 'LuxKit/PricePoints/BusinessTraveller/PriceRowBusinessTravellerUnderBudget'
import BusinessTravellerOfferCreditsUnderBudgetInfoModal from './BusinessTravellerOfferCreditsUnderBudgetInfoModal'
import BusinessTravellerPolicyDebugButton from 'businessTraveller/components/policy-debug/BusinessTravellerPolicyDebugButton'
import Group from 'components/utils/Group'

type SupportedOffer =
  App.Offer |
  App.OfferSummary |
  App.BedbankOffer |
  App.BedbankOfferSummary

type Props = {
  numberOfNights?: number
  offer: SupportedOffer
  totalCost: number
  type: 'exact' | 'estimate'
  roomCount?: number
}

function BusinessTravellerItemPolicyOfferCreditsTextLink(props: Props) {
  const {
    offer,
    totalCost,
    numberOfNights = 1,
    type,
    roomCount,
  } = props
  const showModal = useContext(ModalContext)

  const {
    offerCreditData,
    cacheKey,
    itemPolicyData,
    legacyOfferCredit,
    employeeIds,
  } = useBusinessTravellerItemPolicyOfferCreditFetcher({
    offer,
    numberOfNights,
    cost: totalCost,
    roomCount,
  })

  const showCreditInfoModal = useCallback<MouseEventHandler<HTMLButtonElement>>((event) => {
    event.preventDefault()
    showModal(<BusinessTravellerOfferCreditsInfoModal />)
  }, [showModal])

  const showOverBudgetInfoModal = useCallback<MouseEventHandler<HTMLButtonElement>>((event) => {
    event.preventDefault()
    showModal(<BusinessTravellerHotelCreditsOverBudgetInfoModal numberOfNights={numberOfNights} />)
  }, [numberOfNights, showModal])

  const showUnderBudgetInfoModal = useCallback<MouseEventHandler<HTMLButtonElement>>((event) => {
    event.preventDefault()
    showModal(<BusinessTravellerOfferCreditsUnderBudgetInfoModal />)
  }, [showModal])

  const showUnderBudgetInfo = offerCreditData?.status === 'success' && offerCreditData?.budgetCompliance === 'under_budget'
  const showOverBudgetInfo = offerCreditData?.status === 'success' && offerCreditData?.budgetCompliance === 'over_budget'

  const debugButton = (
    <BusinessTravellerPolicyDebugButton
      source="item_policy"
      vertical="hotel"
      surface="offer_credit"
      primaryData={itemPolicyData}
      comparisonData={legacyOfferCredit}
      cacheKey={cacheKey}
      employeeIds={employeeIds}
    />
  )

  return (
    <Group direction="horizontal" gap={8} verticalAlign="center">
      {(!offerCreditData || offerCreditData.status === 'pending') && <PillLoadingBox width={160}>Loading</PillLoadingBox>}

      {showUnderBudgetInfo && offerCreditData?.creditValue > 0 && (
        <PriceRowBusinessTravellerCredit
          size="M"
          type={type}
          creditValue={offerCreditData.creditValue}
          onClick={showCreditInfoModal}
        />
      )}

      {showUnderBudgetInfo && offerCreditData?.creditValue === 0 && (
        <PriceRowBusinessTravellerUnderBudget
          underPercentage={offerCreditData.budgetComparison}
          onClick={showUnderBudgetInfoModal}
        />
      )}

      {showOverBudgetInfo && (
        <PriceRowBusinessTravellerExceededBudget
          exceedingPercentage={offerCreditData.budgetComparison}
          onClick={showOverBudgetInfoModal}
        />
      )}
      {debugButton}
    </Group>
  )
}

export default BusinessTravellerItemPolicyOfferCreditsTextLink
